import {
  CUSTOMER_TEST_REQUEST_STATUSES,
  type CustomerTestRequestStatus,
} from "@njbsoft/api-client";
import { type components } from "@njbsoft/types/customer";
import { deviceLabel } from "./device-label";
import { enumParam, pageParam, uuidParam } from "./search-params";

type CustomerTestRequest = components["schemas"]["CustomerTestRequest"];
type CustomerInstallation = components["schemas"]["CustomerInstallation"];

/** `/testing/requests`' search. */
export interface RequestsSearch {
  /** A facility id: the feed's server-side narrowing. */
  property?: string;
  /** Applied in the browser: the derived customer status has no query filter. */
  status?: CustomerTestRequestStatus;
  /** A message thread id: the emailed link to one request's conversation. */
  thread?: string;
  /** A page past the first; the first page is the param's absence. */
  page?: number;
}

/**
 * Each param through its validator; anything else, and any value a param
 * cannot hold, is dropped.
 *
 * `?thread={threadId}` is the deep-link contract the new-message email nudges
 * point at: the list finds the request holding the named conversation and
 * opens that request's record, where the conversation sits. A value that
 * cannot be a thread id is dropped rather than looked up or sent to the wire:
 * the read would only spend a request to be told 404, and an attacker-shaped
 * string would ride into the path.
 */
export function validateRequestsSearch(search: Record<string, unknown>): RequestsSearch {
  const property = uuidParam(search.property);
  const status = enumParam(search.status, CUSTOMER_TEST_REQUEST_STATUSES);
  const thread = uuidParam(search.thread);
  const page = pageParam(search.page);
  return {
    ...(property === undefined ? {} : { property }),
    ...(status === undefined ? {} : { status }),
    ...(thread === undefined ? {} : { thread }),
    ...(page === undefined ? {} : { page }),
  };
}

/**
 * The requests at the active account's facilities (the feed takes no account,
 * so `facilityIds` is what narrows it to one), then at `property`, then in
 * `status`, in the order received: newest first.
 */
export function filterRequests(
  requests: readonly CustomerTestRequest[],
  filter: {
    facilityIds: ReadonlySet<string>;
    property?: string;
    status?: CustomerTestRequestStatus;
  },
): CustomerTestRequest[] {
  const { facilityIds, property, status } = filter;
  return requests.filter(
    (r) =>
      facilityIds.has(r.facility_id) &&
      (property === undefined || r.facility_id === property) &&
      (status === undefined || r.status === status),
  );
}

/**
 * The first request holding the thread, at any of the person's accounts, or
 * null. Ids compare without regard to case: a mail client may upper-case the
 * link's hex in transit.
 */
export function findRequestByThread(
  requests: readonly CustomerTestRequest[],
  threadId: string,
): CustomerTestRequest | null {
  const wanted = threadId.toLowerCase();
  return requests.find((r) => r.thread?.id.toLowerCase() === wanted) ?? null;
}

/** The requested device's nameplate name; undefined without a device or a named part. */
export function requestDeviceLabel(
  installation: CustomerInstallation | undefined,
): string | undefined {
  if (!installation) return undefined;
  const name = deviceLabel({
    sizeLabel: installation.size_label,
    assemblyType: installation.assembly_type,
    manufacturerLabel: installation.manufacturer_label,
    modelLabel: installation.model_label,
  });
  return name || undefined;
}
